import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { opsApi, SIDEBAR_BG, GRAD, FONT_HEAD, ROLE_LABEL, ROLE_RANK } from './opsApi';
import OpsDashboard from './OpsDashboard';
import OpsInstitutions from './OpsInstitutions';
import OpsInstitutionDetail from './OpsInstitutionDetail';
import OpsPipeline from './OpsPipeline';
import OpsFleet from './OpsFleet';
import OpsShopHall from './OpsShopHall';
import OpsRevenue from './OpsRevenue';
import OpsApprovals from './OpsApprovals';
import OpsTeamOrg from './OpsTeamOrg';
import OpsPricing from './OpsPricing';
import OpsAudit from './OpsAudit';
import OpsQBManagement from './OpsQBManagement';
import SupportTicketsPage from './SupportTicketsPage';
import SupportTicketDetailPage from './SupportTicketDetailPage';
import {
  LayoutDashboard, Building2, Filter, Cpu, IndianRupee, BadgeCheck,
  Users, Settings2, ScrollText, LogOut, Landmark, MessageSquare, Store, Database,
} from 'lucide-react';

const NAV = [
  { to: '/sales', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/sales/institutions', label: 'Institutions', icon: Building2 },
  { to: '/sales/pipeline', label: 'Sales Pipeline', icon: Filter },
  { to: '/sales/fleet', label: 'Hardware Fleet', icon: Cpu },
  { to: '/sales/shop-hall', label: 'Shop Hall', icon: Store, minRank: 4 },
  { to: '/sales/revenue', label: 'Revenue & Billing', icon: IndianRupee },
  { to: '/sales/approvals', label: 'Approvals', icon: BadgeCheck },
  { to: '/sales/support-tickets', label: 'Support Tickets', icon: MessageSquare },
  { to: '/sales/team', label: 'Team & Org', icon: Users, minRank: 3 },
  { to: '/sales/pricing', label: 'Pricing & Settings', icon: Settings2, minRank: 4 },
  { to: '/sales/qb', label: 'QB Management', icon: Database, minRank: 5 },
  { to: '/sales/audit', label: 'Audit Log', icon: ScrollText, minRank: 5 },
];

export default function OpsApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('desky_ops_token');
    if (!token) {
      navigate('/sales/login');
      return;
    }
    opsApi.get('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => navigate('/sales/login'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('desky_ops_token');
    navigate('/sales/login');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8F7]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#34A853]" />
      </div>
    );
  }

  const rank = ROLE_RANK[user.role] || 0;

  return (
    <div className="min-h-screen flex bg-[#F6F8F7]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Sidebar — white, Deskey native look */}
      <aside className="w-64 shrink-0 flex flex-col fixed inset-y-0 left-0 z-30 bg-white border-r border-slate-200">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-100">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#34A853' }}>
            <Landmark className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight text-slate-900" style={FONT_HEAD}>Desk Ops</div>
            <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#34A853' }}>{ROLE_LABEL[user.role]}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.minRank || rank >= n.minRank).map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`ops-nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'font-semibold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
              style={({ isActive }) => (isActive ? { backgroundColor: '#E8F5E9', color: '#2D9249' } : undefined)}>
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="px-2 pb-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Scope</span>
            <div data-testid="ops-user-scope" className="text-xs font-mono text-slate-700 truncate">{user.scope_path}</div>
          </div>
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 text-white" style={{ backgroundColor: '#34A853' }}>
              {user.full_name?.split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-800 truncate" data-testid="ops-user-name">{user.full_name}</div>
              <div className="text-[10px] text-slate-400 truncate">{user.email}</div>
            </div>
            <button onClick={logout} data-testid="ops-logout-btn" title="Sign out"
              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-w-0">
        <div className="max-w-[1320px] mx-auto px-8 py-8">
          <Routes>
            <Route index element={<OpsDashboard user={user} />} />
            <Route path="institutions" element={<OpsInstitutions user={user} />} />
            <Route path="institutions/:instId" element={<OpsInstitutionDetail user={user} />} />
            <Route path="pipeline" element={<OpsPipeline user={user} />} />
            <Route path="fleet" element={<OpsFleet user={user} />} />
            {rank >= 4 && <Route path="shop-hall" element={<OpsShopHall user={user} />} />}
            <Route path="revenue" element={<OpsRevenue user={user} />} />
            <Route path="approvals" element={<OpsApprovals user={user} />} />
            <Route path="support-tickets" element={<SupportTicketsPage user={user} />} />
            <Route path="support-tickets/:ticketId" element={<SupportTicketDetailPage user={user} />} />
            {rank >= 3 && <Route path="team" element={<OpsTeamOrg user={user} />} />}
            {rank >= 4 && <Route path="pricing" element={<OpsPricing user={user} />} />}
            {rank >= 5 && <Route path="audit" element={<OpsAudit user={user} />} />}
            {rank >= 5 && <Route path="qb" element={<OpsQBManagement user={user} />} />}
            <Route path="*" element={<Navigate to="/sales" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
